import { TextField } from '@mui/material';
import { useContext, useEffect, useReducer, useRef, useState } from 'react';
import { AuthenticatedSessionContext } from '../../authenticatedSession/AuthenticatedSessionContext';
import type { AuthenticatedSession } from '../../authenticatedSession/authenticatedSession';
import { textFieldStyles } from '../../muiStyles/textFieldStyles';
import MatterAutocomplete from '../MatterAutocomplete';
import '../Modal.css';
import UserAutocomplete from '../UserAutocomplete';
import styles from './TimeEntryModal.module.css';
import { defaultState, timeEntryFormReducer } from './reducer';
import { flushSync } from 'react-dom';
import DateTextField from '../DateTextField/DateTextField';
import { QuickEntryModal } from '../QuickEntryModal/QuickEntryModal';
import { TimeEntryData, TimeEntryDocument } from '../../database/baseTypes';
import { useTimeEntryAdd } from '../../database/hooks/hooks';
import {
  ymdFromLocalDate,
  ymdIsEqualTo,
  ymdToDate,
} from '@/database/yearMonthDayUtil';
import { DocumentReference, deleteDoc } from 'firebase/firestore';
import { formatCurrency, validNumber } from '@/utils/numberFormatters';
import NonIncrementingNumberInput from '../NonIncrementingNumberInput';
import { Button, Group, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { validateEarnedYmd } from '../../views/Fees/TimeEntryRow/rowValidation';
import { auditingEventForTimeEntryField } from '../../views/Fees/TimeEntryRow/auditingEventForTimeEntryField';

interface TimeEntryModalProps {
  fixedMatterId: string | undefined;
  entry?: TimeEntryDocument;
  initialHours?: number;
  onClose: (submitted?: boolean) => void;
}

const TimeEntryModal = ({
  fixedMatterId,
  entry,
  initialHours,
  onClose,
}: TimeEntryModalProps) => {
  const authSession = useContext(AuthenticatedSessionContext);
  const [state, dispatch] = useReducer(
    timeEntryFormReducer,
    defaultState(
      entry?.user ?? authSession?.userIds?.firestore,
      undefined,
      entry ? ymdToDate(entry.earnedYmd) : new Date(),
      entry ? validNumber(entry.hours) : initialHours,
      entry ? validNumber(entry.rate) : undefined,
      entry?.description,
    ),
  );
  const startingInputRef = useRef<any>(null);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    if (!isSaving) onClose(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if ((event.target as Element)?.className === 'modal-backdrop') {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClose]);

  useEffect(() => {
    startingInputRef.current?.focus();
  }, []);

  const addTimeEntry = useTimeEntryAdd();

  const handleSave = async (): Promise<boolean> => {
    if (!authSession) {
      return false;
    }

    let hasErrors = false;
    if (state.hours.value == null) {
      hasErrors = true;
      dispatch({ type: 'SET_HOURS_ERROR', payload: 'Required' });
    }
    if (state.rate.value == null) {
      hasErrors = true;
      dispatch({ type: 'SET_RATE_ERROR', payload: 'Required ' });
    }
    if (!state.date.value) {
      hasErrors = true;
      dispatch({ type: 'SET_DATE_ERROR', payload: 'Required' });
    }
    const ymd = ymdFromLocalDate(state.date.value!);
    const dateError = ymd ? validateEarnedYmd(ymd) : undefined;
    if (dateError) {
      hasErrors = true;
      dispatch({ type: 'SET_DATE_ERROR', payload: dateError });
    }

    if (!fixedMatterId && !state.matter.value) {
      hasErrors = true;
      dispatch({ type: 'SET_MATTER_ERROR', payload: 'Required' });
    }
    if (!state.userId.value) {
      hasErrors = true;
      dispatch({ type: 'SET_USER_ERROR', payload: 'Required' });
    }
    if (!state.description.value) {
      hasErrors = true;
      dispatch({ type: 'SET_DESCRIPTION_ERROR', payload: 'Required' });
    }

    if (hasErrors) {
      notifications.show({
        title: 'Missing required fields',
        message: 'Highlighted fields are required',
        color: 'red.4',
        autoClose: 6000,
      });
      return false;
    }

    const newTimeEntry: TimeEntryData = {
      earnedYmd: ymd!,
      hours: state.hours.value ?? 0,
      rate: state.rate.value ?? 0,
      matter: fixedMatterId ?? state.matter.value!.key,
      description: state.description.value!,
      user: state.userId.value!,
      createdTimestamp: new Date(),
      createdBy: authSession.userIds.firestore,
    };
    if (entry) {
      try {
        setIsSaving(true);
        const data: Partial<TimeEntryData> = {
          earnedYmd: newTimeEntry.earnedYmd,
          hours: newTimeEntry.hours,
          rate: newTimeEntry.rate,
          description: newTimeEntry.description,
          user: newTimeEntry.user,
        };
        const updateEvents = await timeEntryEditEvents({
          authSession,
          entry,
          newTimeEntry,
        });
        if (updateEvents.length > 0) {
          await authSession.db.updateTimeEntryAndAppendEvents(
            entry.id,
            data,
            updateEvents,
          );
        } else {
          await authSession.db.updateTimeEntry(entry.id, data);
        }
        notifications.show({
          title: 'Time entry updated',
          message: 'The time entry was updated.',
          color: 'teal',
        });
        return true;
      } finally {
        setIsSaving(false);
      }
    }
    const ref = await addTimeEntry({
      ...newTimeEntry,
      hours: state.hours.value || 0,
    });
    const successNotificationId = `new-time-entry-${ref?.id ?? Date.now()}`;

    notifications.show({
      id: successNotificationId,
      title: 'Time entry created',
      message: (
        <Group justify='space-between' wrap='nowrap'>
          <span>New time entry created successfully</span>
          <Button
            variant='white'
            color='dark'
            size='compact-xs'
            onClick={async () => {
              notifications.hide(successNotificationId);
              await handleUndo(ref);
            }}
          >
            Undo
          </Button>
        </Group>
      ),
      color: 'blue.4',
      autoClose: 6000,
    });

    flushSync(() => {
      dispatch({ type: 'CLEAR_FORM', payload: undefined });
    });

    startingInputRef.current?.focus();
    return true;
  };

  const handleUndo = async (
    refToDelete: DocumentReference<TimeEntryData> | undefined,
  ) => {
    if (!refToDelete) {
      return;
    }
    await deleteDoc(refToDelete);
    notifications.show({
      title: 'Undo successful',
      message: 'Time entry has been removed',
      color: 'blue.4',
      autoClose: 1500,
    });
  };

  return (
    <QuickEntryModal
      title={entry ? 'Edit Time Entry' : 'New Time Entry'}
      saveButtonDisabled={isSaving}
      onClose={handleClose}
      onSave={async () => {
        const hasSaved = await handleSave();
        if (hasSaved) {
          onClose(true);
        }
      }}
    >
      <UserAutocomplete
        firmDb={authSession?.db}
        error={!!state.userId.error}
        selectedUserId={state.userId.value}
        inputRef={startingInputRef}
        onSelect={(userId) =>
          dispatch({ type: 'SET_USER_VAL', payload: userId })
        }
      />
      {!fixedMatterId && (
        <MatterAutocomplete
          firmDb={authSession?.db}
          error={!!state.matter.error}
          value={state.matter.value}
          onSelect={(matter) => {
            dispatch({ type: 'SET_MATTER_VAL', payload: matter });
          }}
        />
      )}
      <DateTextField
        value={state.date.value ?? null}
        error={!!state.date.error}
        required={true}
        onChange={(value) =>
          dispatch({ type: 'SET_DATE_VAL', payload: value ?? undefined })
        }
      />
      <div className={styles.rateHoursAmountRow}>
        <NonIncrementingNumberInput
          prefix='$'
          flex={'1 1 0'}
          decimalScale={2}
          fixedDecimalScale={true}
          thousandSeparator={true}
          label='$ Rate'
          required={true}
          value={state.rate.value ?? ''}
          onChange={(value) =>
            dispatch({ type: 'SET_RATE_VAL', payload: value ?? undefined })
          }
          error={!!state.rate.error}
        />
        <NonIncrementingNumberInput
          decimalScale={1}
          flex={'1 1 0'}
          fixedDecimalScale={true}
          label='Hours'
          required={true}
          value={state.hours.value ?? ''}
          onChange={(value) =>
            dispatch({ type: 'SET_HOURS_VAL', payload: value ?? undefined })
          }
          error={!!state.hours.error}
        />
        <Stack gap='4px' flex={'1 1 0'} p='8px'>
          <Text size='sm' fw={500}>
            Amount
          </Text>
          <Text size='sm'>{formatCurrency(state.amount)}</Text>
        </Stack>
      </div>
      <TextField
        sx={textFieldStyles}
        label='Description'
        multiline
        rows={4}
        error={!!state.description.error}
        value={state.description.value ?? ''}
        required={true}
        onChange={(e) =>
          dispatch({ type: 'SET_DESCRIPTION_VAL', payload: e.target.value })
        }
      />
    </QuickEntryModal>
  );
};

export default TimeEntryModal;

const timeEntryEditEvents = async ({
  authSession,
  entry,
  newTimeEntry,
}: {
  authSession: AuthenticatedSession;
  entry: TimeEntryDocument;
  newTimeEntry: TimeEntryData;
}) => {
  const users = await authSession.db.getUsers();
  const userMap = Object.fromEntries(
    users.docs.map((doc) => {
      const user = doc.data();
      return [
        doc.id,
        {
          firstName: user.firstName,
          lastName: user.lastName,
          fullName: `${user.firstName} ${user.lastName}`,
        },
      ];
    }),
  );
  const eventFor = (field: keyof TimeEntryData) =>
    auditingEventForTimeEntryField({
      field,
      previousValue: entry[field],
      value: newTimeEntry[field],
      authSession,
      userMap,
    });
  return [
    ...(!ymdIsEqualTo({ ymd: entry.earnedYmd, other: newTimeEntry.earnedYmd })
      ? [eventFor('earnedYmd')]
      : []),
    ...(validNumber(entry.hours) !== validNumber(newTimeEntry.hours)
      ? [eventFor('hours')]
      : []),
    ...(validNumber(entry.rate) !== validNumber(newTimeEntry.rate)
      ? [eventFor('rate')]
      : []),
    ...(entry.description !== newTimeEntry.description
      ? [eventFor('description')]
      : []),
    ...(entry.user !== newTimeEntry.user ? [eventFor('user')] : []),
  ];
};
